import { useEffect, useState } from 'react';
import { useDebouncedEffect } from '../debounce';
import { CommonUtility, ShipmentsService } from '../../utility';
import { calculatePublicCBM, calculatePublicTotal } from '@utility/utils/shipment';
import {
  ShipmentListResponseSchema,
  type ShipmentListType,
} from 'schemas/shipment/inbound/list';
import {
  WmsShipmentDetailSchema,
  type WmsShipmentInboundType,
} from 'schemas/shipment/inbound/detail';

type ShipmentListReturnType = {
  data: ShipmentListType[];
  error: any;
  filter: any;
  filterChanged: (key: string, value: any) => void;
  loading: boolean;
  pageChanged: (page: number) => void;
  setSearch: (search: string) => void;
  totalData: number;
  totalPages: number;
  refresh: () => void;
};

export const ShipmentListHook = (): ShipmentListReturnType => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ShipmentListType[]>([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
      } as any;

      if (temp.search) {
        temp.search = temp.search.trim();
      }

      const res = await ShipmentsService.get(temp);
      const parsedRes = ShipmentListResponseSchema.parse(res);
      setData(parsedRes?.data);
      setTotalData(parsedRes?.totalCount);
      setTotalPages(parsedRes?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      fetchData();
    },
    [filter, refreshKey],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
  };
};

type ShipmentDetailReturnType = {
  data: WmsShipmentInboundType;
  error: any;
  loading: boolean;
  refresh: () => void;
  setLoading: (loading: boolean) => void;
};

export const ShipmentDetailHook = (id?: string): ShipmentDetailReturnType => {
  const [loading, setLoading] = useState(Boolean(id));
  const [data, setData] = useState<WmsShipmentInboundType>({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await ShipmentsService.getById(id);
      const parsedRes = WmsShipmentDetailSchema.parse(res);
      setData(parsedRes || {});
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  return { data, loading, error, refresh: fetchData, setLoading };
};

export const shipmentDetailsExtractor = data => {
  const [cbm, setCBM] = useState(0);
  const [summary, setSummary] = useState({
    cartons: 0,
    units: 0,
    retails: 0,
    grossWeight: 0,
  });

  useDebouncedEffect(
    () => {
      if (CommonUtility.isValidObject(data)) {
        const newCBM = calculatePublicCBM(data.items);
        const newTotal = calculatePublicTotal(data.items);

        setCBM(newCBM);
        setSummary(newTotal);
      }
    },
    [data],
    200,
  );

  return { summary, cbm };
};
